import { Box, Typography } from '@mui/material'
import "@fontsource/rubik";
import { formatHistoryDateTime, formatTimelineLabel } from '../../../utils/revisionHistory'

/**
 * Vertical timeline of what happened to one version: a rail of dots joined by a
 * connecting line, oldest at the top.
 *
 * The API already sorts the entries (timed first, oldest first, then the
 * `at: null` ones), so they render in the order they arrive - no client-side
 * sorting.
 *
 * Entries the backend has no time for - `accepted` and `sent_for_approval` in
 * Phase 1 - get a hollow dot instead of a filled one. They sit at the end of the
 * rail by definition, and the hollow marker says "this happened, we just don't
 * know when" rather than silently showing nothing where a date would be.
 */
const RevisionHistoryTimeline = (props) => {
  const { timeline, version } = props;
  const entries = Array.isArray(timeline) ? timeline : [];

  return (
    <Box textAlign='left'>
      <Typography
        sx={{ fontWeight: 600, fontFamily: 'Rubik', color: 'black', fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' }, mb: 1 }}
      >
        Activity
      </Typography>

      {entries.length === 0 ? (
        <Typography
          data-testid='revision-history-timeline-empty'
          sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' } }}
        >
          No activity recorded for this version
        </Typography>
      ) : (
        entries.map((entry, index) => {
          const timeLabel = formatHistoryDateTime(entry?.at);
          const isLast = index === entries.length - 1;
          return (
            <Box
              key={`${entry?.action}-${entry?.note_id || ''}-${entry?.at || ''}-${index}`}
              data-testid='revision-history-timeline-entry'
              sx={{ display: 'flex', gap: 1.2, alignItems: 'stretch' }}
            >
              {/* The rail: a marker, then a line stretching to the next marker.
                  The row is stretch-aligned, so the line spans whatever height
                  the label beside it takes. */}
              <Box className='flex col a-center' sx={{ flexShrink: 0 }}>
                <Box
                  data-testid='revision-history-timeline-dot'
                  data-timed={timeLabel ? 'true' : 'false'}
                  sx={{
                    width: { xs: '7px', xl: '9px' },
                    height: { xs: '7px', xl: '9px' },
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: '1px solid #08231B',
                    backgroundColor: timeLabel ? '#08231B' : 'transparent',
                    marginTop: { xs: '3px', xl: '5px' },
                    flexShrink: 0,
                  }}
                />
                {!isLast && (
                  <Box
                    data-testid='revision-history-timeline-connector'
                    sx={{ flexGrow: 1, width: '1px', backgroundColor: '#08231B', opacity: 0.18, marginTop: '2px' }}
                  />
                )}
              </Box>

              <Box sx={{ paddingBottom: isLast ? 0 : 1.2 }}>
                <Typography
                  sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '8px', md: '10px', lg: '10px', xl: '13px' }, wordBreak: 'break-word' }}
                >
                  {formatTimelineLabel(entry, version)}
                </Typography>
                {!!timeLabel && (
                  <Typography
                    sx={{ fontFamily: 'Rubik', color: '#08231B', opacity: 0.65, fontStyle: 'italic', fontSize: { xs: '7px', md: '9px', lg: '9px', xl: '12px' } }}
                  >
                    {timeLabel}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  )
}

export default RevisionHistoryTimeline
